/**
 * Unified widget initialization and rendering utility
 * This function ensures widgets are always initialized before rendering
 * Use this function everywhere widgets need to be rendered in the codebase
 */

import type React from 'react';
import { getWidgetIdFromTemplate, isMultiWidgetTemplate } from 'constants/web-widget-mapping';

// Serialize widget render calls across the app to avoid crashing the SDK when multiple widgets
// are rendered simultaneously (e.g., "View All Web Widgets" renders 5 at once).
let widgetRenderQueue: Promise<any> = Promise.resolve();

export interface WidgetRenderParams {
  templateKey: string;
  scenarioId: string;
  instanceId: string | null;
  /** When reinitialize is used, pass the context's instanceIdRef so the renderer can read the new instanceId after reinit. */
  instanceIdRef?: React.MutableRefObject<string | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  reinitialize?: () => Promise<void>;
  onRenderComplete?: (response: any) => void;
  onError?: (error: Error) => void;
}

export interface WidgetRenderResult {
  success: boolean;
  widgetTag?: string;
  widgetTags?: string[]; // For multi-widget renders
  error?: string;
}

/**
 * Initialize and render a widget
 * This function:
 * 1. Re-initializes the SDK if reinitialize function is provided (ensures fresh data)
 * 2. Maps templateKey to widgetId
 * 3. Gets widget tag from SDK
 * 4. Clears container and creates widget element
 * 5. Calls renderWidget with proper error handling
 * 
 * @param params - Widget rendering parameters
 * @returns Promise resolving to render result
 */
export async function initializeAndRenderWidget(
  params: WidgetRenderParams
): Promise<WidgetRenderResult> {
  const {
    templateKey,
    scenarioId,
    instanceId,
    instanceIdRef,
    containerRef,
    reinitialize,
    onRenderComplete,
    onError
  } = params;

  try {
    // Step 1: Re-initialize SDK if function is provided (for fresh data on menu item clicks, saves, or scenario changes)
    if (reinitialize) {
      await reinitialize();
    }

    // After reinit, use the current instanceId from ref if provided (context updates the ref when instanceId state changes)
    const effectiveInstanceId = instanceIdRef?.current ?? instanceId;

    // Step 2: Validate inputs
    if (!templateKey) {
      const error = new Error('Template key is required');
      onError?.(error);
      return { success: false, error: error.message };
    }

    if (!effectiveInstanceId) {
      const error = new Error('Instance ID is required - SDK not initialized');
      onError?.(error);
      return { success: false, error: error.message };
    }

    if (!scenarioId) {
      const error = new Error('Scenario ID is required');
      onError?.(error);
      return { success: false, error: error.message };
    }

    if (!containerRef.current) {
      const error = new Error('Container ref is not available');
      onError?.(error);
      return { success: false, error: error.message };
    }

    // Step 3: Get widget ID from template key
    // If mapping is missing, attempt a safe runtime heuristic based on SDK WIDGET_TAG keys.
    let widgetKey = getWidgetIdFromTemplate(templateKey) as unknown as string | null;
    if (!widgetKey) {
      const candidateFromTemplate = (templateKey.split('.').pop() || '').toUpperCase(); // e.g. "APPLIANCE"
      const tags = (window as any)?.BidgelyWebSdk?.WIDGET_TAG as Record<string, string> | undefined;
      if (tags && candidateFromTemplate) {
        // Prefer exact key match, else a single contains match.
        if (tags[candidateFromTemplate]) {
          widgetKey = candidateFromTemplate;
        } else {
          const contains = Object.keys(tags).filter(k => k.toUpperCase().includes(candidateFromTemplate));
          if (contains.length === 1) {
            widgetKey = contains[0];
          }
        }
      }

      if (!widgetKey) {
        const error = new Error(`No widget mapping for template: ${templateKey}`);
        console.warn('[widget-renderer]', error.message);
        onError?.(error);
        return { success: false, error: error.message };
      }
    }

    // Step 4: Check if SDK is available
    if (!window.BidgelyWebSdk) {
      const error = new Error('BidgelyWebSdk not available');
      console.error('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    // Step 5: Get widget tag from SDK
    // Note: WIDGET_TAG is typed with a finite key union, but at runtime we may resolve widget keys dynamically.
    // Treat it as a string map for safe indexing.
    const widgetTags = (window.BidgelyWebSdk?.WIDGET_TAG as unknown as Record<string, string> | undefined);
    const widgetTag = widgetKey ? widgetTags?.[widgetKey] : undefined;
    if (!widgetTag) {
      const error = new Error(`Widget tag not found for: ${widgetKey}`);
      console.warn('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    // Step 6: Clear container and create widget element
    const container = containerRef.current;
    container.innerHTML = '';

    // Step 7: Create widget element in DOM
    container.innerHTML = `<${widgetTag} instanceId="${effectiveInstanceId}"></${widgetTag}>`;

    // Step 8: Render widget after element is in DOM
    const renderTask = () => new Promise<WidgetRenderResult>((resolve) => {
      // Note: keep this async boundary very small; we mainly want to serialize renderWidget calls.
      queueMicrotask(() => {
        try {
          if (!containerRef.current || !window.BidgelyWebSdk?.renderWidget) {
            const error = new Error('Container or renderWidget not available');
            onError?.(error);
            resolve({ success: false, error: error.message });
            return;
          }

          window.BidgelyWebSdk.renderWidget(
            {
              widgetId: widgetTag,
              instanceId: effectiveInstanceId,
              scenarioId: scenarioId,
            },
            (response: any) => {
              try {
                if (response.messageType === 'ERROR') {
                  const error = new Error(`Widget render error: ${response.data || 'Unknown error'}`);
                  console.error('[widget-renderer]', error.message);
                  onError?.(error);
                  resolve({ success: false, error: error.message, widgetTag });
                  return;
                }

                // Success
                onRenderComplete?.(response);
                resolve({ success: true, widgetTag });
              } catch (callbackErr) {
                const error = callbackErr instanceof Error 
                  ? callbackErr 
                  : new Error(String(callbackErr));
                console.error('[widget-renderer] Error in renderWidget callback:', error);
                onError?.(error);
                resolve({ success: false, error: error.message, widgetTag });
              }
            }
          );
        } catch (renderErr) {
          const error = renderErr instanceof Error 
            ? renderErr 
            : new Error(String(renderErr));
          console.error('[widget-renderer] Error rendering widget:', error);
          
          // Clear container on error
          if (containerRef.current) {
            containerRef.current.innerHTML = '';
          }
          
          onError?.(error);
          resolve({ success: false, error: error.message });
        }
      });
    });

    // Serialize renderWidget calls to avoid SDK crashes under parallel rendering.
    const queued = widgetRenderQueue.then(renderTask, renderTask);
    widgetRenderQueue = queued.then(
      () => undefined,
      () => undefined
    );
    return queued;
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    console.error('[widget-renderer] Fatal error:', error);
    
    // Clear container on error
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    
    onError?.(error);
    return { success: false, error: error.message };
  }
}

/**
 * Initialize and render multiple widgets for a multi-widget template
 * This function:
 * 1. Re-initializes the SDK if reinitialize function is provided
 * 2. Maps templateKey to array of widgetIds
 * 3. Creates multiple widget elements in the container
 * 4. Renders all widgets sequentially
 *
 * @param params - Widget rendering parameters (same as initializeAndRenderWidget)
 * @returns Promise resolving to render result with all widget tags
 */
export async function initializeAndRenderMultipleWidgets(
  params: WidgetRenderParams
): Promise<WidgetRenderResult> {
  const {
    templateKey,
    scenarioId,
    instanceId,
    instanceIdRef,
    containerRef,
    reinitialize,
    onRenderComplete,
    onError
  } = params;

  try {
    // Step 1: Re-initialize SDK if needed (same as single widget)
    let effectiveInstanceId = instanceId;
    if (reinitialize && instanceIdRef) {
      console.log('[widget-renderer] Re-initializing SDK for multi-widget template:', templateKey);
      await reinitialize();
      effectiveInstanceId = instanceIdRef.current;
      if (!effectiveInstanceId) {
        const error = new Error('Re-initialization failed: instanceId is null');
        console.error('[widget-renderer]', error.message);
        onError?.(error);
        return { success: false, error: error.message };
      }
    }

    if (!effectiveInstanceId) {
      const error = new Error('Instance ID is required');
      console.error('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    // Step 2: Get widget IDs from template key
    const widgetIds = getWidgetIdFromTemplate(templateKey);

    console.log('[widget-renderer] Multi-widget template:', templateKey, '→', widgetIds);

    if (!widgetIds || !Array.isArray(widgetIds) || widgetIds.length === 0) {
      const error = new Error(`No multi-widget mapping for template: ${templateKey}`);
      console.warn('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    // Step 3: Check if SDK is available
    if (!window.BidgelyWebSdk) {
      const error = new Error('BidgelyWebSdk not available');
      console.error('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    // Step 4: Get widget tags from SDK
    const widgetTags = (window.BidgelyWebSdk?.WIDGET_TAG as unknown as Record<string, string> | undefined);
    if (!widgetTags) {
      const error = new Error('WIDGET_TAG not available in SDK');
      console.error('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    const resolvedWidgetTags: string[] = [];
    for (const widgetKey of widgetIds) {
      const widgetTag = widgetTags[widgetKey];
      if (!widgetTag) {
        const error = new Error(`Widget tag not found for: ${widgetKey}`);
        console.warn('[widget-renderer]', error.message);
        onError?.(error);
        return { success: false, error: error.message };
      }
      console.log('[widget-renderer] Resolved widget:', widgetKey, '→', widgetTag);
      resolvedWidgetTags.push(widgetTag);
    }

    console.log('[widget-renderer] All resolved widget tags:', resolvedWidgetTags);

    // Step 5: Clear container and create all widget elements
    const container = containerRef.current;
    if (!container) {
      const error = new Error('Container ref is null');
      console.error('[widget-renderer]', error.message);
      onError?.(error);
      return { success: false, error: error.message };
    }

    container.innerHTML = '';

    // Create all widget elements directly in the container with spacing
    // Build HTML string with all widgets
    const widgetsHtml = resolvedWidgetTags.map((widgetTag, index) => {
      const marginStyle = index < resolvedWidgetTags.length - 1 ? ' style="margin-bottom: 16px;"' : '';
      return `<${widgetTag} instanceId="${effectiveInstanceId}"${marginStyle}></${widgetTag}>`;
    }).join('\n');

    container.innerHTML = widgetsHtml;

    console.log('[widget-renderer] Created multi-widget elements:', resolvedWidgetTags.length, 'widgets');

    // Step 6: Render all widgets sequentially using the queue
    const renderResults: Array<{ success: boolean; widgetTag: string; error?: string }> = [];

    console.log('[widget-renderer] Starting to render', resolvedWidgetTags.length, 'widgets sequentially...');

    for (let i = 0; i < resolvedWidgetTags.length; i++) {
      const widgetTag = resolvedWidgetTags[i];
      console.log(`[widget-renderer] Rendering widget ${i + 1}/${resolvedWidgetTags.length}:`, widgetTag);

      const renderTask = () => new Promise<{ success: boolean; widgetTag: string; error?: string }>((resolve) => {
        queueMicrotask(() => {
          try {
            if (!containerRef.current || !window.BidgelyWebSdk?.renderWidget) {
              const error = new Error('Container or renderWidget not available');
              resolve({ success: false, widgetTag, error: error.message });
              return;
            }

            window.BidgelyWebSdk.renderWidget(
              {
                widgetId: widgetTag,
                instanceId: effectiveInstanceId!,
                scenarioId: scenarioId,
              },
              (response: any) => {
                try {
                  if (response.messageType === 'ERROR') {
                    const error = new Error(`Widget render error: ${response.data || 'Unknown error'}`);
                    console.error('[widget-renderer]', error.message, 'for widget:', widgetTag);
                    resolve({ success: false, widgetTag, error: error.message });
                    return;
                  }

                  // Success
                  console.log('[widget-renderer] Successfully rendered widget:', widgetTag);
                  resolve({ success: true, widgetTag });
                } catch (callbackErr) {
                  const error = callbackErr instanceof Error
                    ? callbackErr
                    : new Error(String(callbackErr));
                  console.error('[widget-renderer] Error in renderWidget callback:', error);
                  resolve({ success: false, widgetTag, error: error.message });
                }
              }
            );
          } catch (renderErr) {
            const error = renderErr instanceof Error
              ? renderErr
              : new Error(String(renderErr));
            console.error('[widget-renderer] Error rendering widget:', error);
            resolve({ success: false, widgetTag, error: error.message });
          }
        });
      });

      // Serialize renderWidget calls to avoid SDK crashes
      const queued = widgetRenderQueue.then(renderTask, renderTask);
      widgetRenderQueue = queued.then(
        () => undefined,
        () => undefined
      );
      const result = await queued;
      renderResults.push(result);
    }

    // Check if all widgets rendered successfully
    const allSuccess = renderResults.every(r => r.success);
    const errors = renderResults.filter(r => !r.success).map(r => r.error).join('; ');

    console.log('[widget-renderer] Multi-widget render complete. Success:', allSuccess, 'Results:', renderResults);

    if (allSuccess) {
      onRenderComplete?.({ messageType: 'SUCCESS', data: 'All widgets rendered' });
      return { success: true, widgetTags: resolvedWidgetTags };
    } else {
      const error = new Error(`Some widgets failed to render: ${errors}`);
      console.error('[widget-renderer] Multi-widget render failed:', error.message);
      onError?.(error);
      return { success: false, widgetTags: resolvedWidgetTags, error: error.message };
    }

  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    console.error('[widget-renderer] Fatal error in multi-widget rendering:', error);

    // Clear container on error
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    onError?.(error);
    return { success: false, error: error.message };
  }
}

